import { put, call, all, takeEvery, takeLatest } from 'redux-saga/effects'
import { store as reduxStore } from 'index'
import { push } from 'react-router-redux'
import { message } from 'antd'
import {
  collaborators as appCollaboratorsList,
  invite as collaboratorAdd,
  remove as collaboratorRemove,
  changeRole as collaboratorChangeRole,
  leave as collaboratorLeave,
  transferOwnership as collaboratorTransferOwnership,
  search as collaboratorsSearch,
} from 'services/teams'
import { get } from 'services/apps'
import _ from 'lodash'
import constants from 'constants'
import { isAuthErrorCode, isAppAdmin, isAppOwner } from 'helpers'
import actions from './actions'

function* handleAuthError(error) {
  if (error && error.code && isAuthErrorCode(error.code)) {
    if (error.code === 'AUTH_ERR_TOKEN_BLACKLISTED') {
      yield put({ type: 'user/LOGOUT' })
    } else {
      yield put(push('/login'))
    }
  }
}

export function* TEAM_COLLABORATOR_LIST({ payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areTeamMembersLoading: true },
  })

  const activeApp = payload.data || reduxStore.getState().apps.active
  const userData = reduxStore.getState().user
  const { teamCollaboratorSearchText } = reduxStore.getState().teams

  try {
    const success = yield call(appCollaboratorsList, activeApp, payload.currentPage, teamCollaboratorSearchText)

    if (success && success.data) {
      let totalCollaboratorCount = success.data.length
      if (success.meta && success.meta.pagination && success.meta.pagination.total) {
        totalCollaboratorCount = success.meta.pagination.total
      }

      let sortedList = success.data
      if (sortedList.length > 0) {
        const loggedInCollaboratorIndex = _.findIndex(sortedList, function fn(sl) {
          return _.toLower(sl.email) === _.toLower(userData.email)
        })
        if (loggedInCollaboratorIndex > 0) {
          const loggedInCollaboratorValue = sortedList[loggedInCollaboratorIndex]
          _.pullAt(sortedList, [loggedInCollaboratorIndex])
          sortedList = _.concat(sortedList, loggedInCollaboratorValue)
          sortedList = _.reverse(sortedList)
        }
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          teamCollaboratorsList: sortedList,
          teamCollaboratorsListTotal: totalCollaboratorCount,
          areTeamMembersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          teamCollaboratorsList: [],
          teamCollaboratorsListTotal: 0,
          areTeamMembersLoading: false,
        },
      })
      if (success && success.error) {
        window.triggerNotification(success.errDescription || 'Failed to fetch team members', 'error')
        yield* handleAuthError(success.error)
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: { areTeamMembersLoading: false },
    })
    window.triggerNotification('Failed to fetch team members', 'error')
  }
}

export function* TEAM_COLLABORATOR_INVITE({ payload, appCreated }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areTeamMembersLoading: true },
  })

  let activeApp = appCreated
  if (activeApp === undefined) {
    activeApp = reduxStore.getState().apps.active
  }

  if (!appCreated && !(activeApp.role !== undefined && (isAppAdmin(activeApp) || isAppOwner(activeApp)))) {
    window.triggerNotification('Access Denied - you are not authorized to perform this action', 'error')
    yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
    yield put(push(`/app/${activeApp.id}/members`))
    return
  }

  try {
    const success = yield call(collaboratorAdd, payload, activeApp)

    if (success && success.data) {
      yield put({
        type: actions.TEAM_COLLABORATOR_LIST,
        payload: { currentPage: 1, data: activeApp },
      })

      let invitationSuccess = false
      let invitationActionMsg = 'Invitation not sent. Please try again.'

      if (payload.admins && payload.admins.length > 0) {
        const invitationEmail = payload.admins[0]
        if (success.data.admins && success.data.admins[invitationEmail]) {
          const result = success.data.admins[invitationEmail]
          if (result.success && result.sentInvitation) {
            invitationSuccess = true
          } else if (result.success && result.alreadyAcceptedInvite && result.collaboratorExists) {
            invitationActionMsg = 'This user is an existing collaborator'
          } else if (result.error) {
            if (result.error.code === 'ERR_SELF_ACTION_DENIED') {
              invitationActionMsg = 'You are already collaborating in this app! :)'
            } else if (result.error.code === 'ERR_ALREADY_AN_OWNER') {
              invitationActionMsg = 'This user is an existing collaborator (owner)'
            } else {
              invitationActionMsg = result.error.message
            }
          }
        }
      } else if (payload.developers && payload.developers.length > 0) {
        const invitationEmail = payload.developers[0]
        if (success.data.developers && success.data.developers[invitationEmail]) {
          const result = success.data.developers[invitationEmail]
          if (result.success && result.sentInvitation) {
            invitationSuccess = true
          } else if (result.success && result.alreadyAcceptedInvite && result.collaboratorExists) {
            invitationActionMsg = 'This user is an existing collaborator'
          } else if (result.error) {
            if (result.error.code === 'ERR_SELF_ACTION_DENIED') {
              invitationActionMsg = 'You are already collaborating in this app! :)'
            } else if (result.error.code === 'ERR_ALREADY_AN_OWNER') {
              invitationActionMsg = 'This user is an existing collaborator (owner)'
            } else {
              invitationActionMsg = result.error.message
            }
          }
        }
      } else if (payload.moderators && payload.moderators.length > 0) {
        const invitationEmail = payload.moderators[0]
        if (success.data.moderators && success.data.moderators[invitationEmail]) {
          const result = success.data.moderators[invitationEmail]
          if (result.success && result.sentInvitation) {
            invitationSuccess = true
          } else if (result.success && result.alreadyAcceptedInvite && result.collaboratorExists) {
            invitationActionMsg = 'This user is an existing collaborator'
          } else if (result.error) {
            if (result.error.code === 'ERR_SELF_ACTION_DENIED') {
              invitationActionMsg = 'You are already collaborating in this app! :)'
            } else if (result.error.code === 'ERR_ALREADY_AN_OWNER') {
              invitationActionMsg = 'This user is an existing collaborator (owner)'
            } else {
              invitationActionMsg = result.error.message
            }
          }
        }
      }

      if (invitationSuccess) {
        window.triggerNotification('Invitation sent', 'success')
      } else {
        window.triggerNotification(invitationActionMsg, 'error')
      }
    } else {
      yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
      if (success.error) {
        if (success.error.code === 'ERR_BAD_REQUEST') {
          _.forEach(success.error.details, function fn(value) {
            message.error(value)
          })
        } else if (success.error.code === 'ERR_ACCESS_DENIED') {
          window.triggerNotification('Access Denied - you are not authorized to perform this action', 'error')
        } else if (success.error.message) {
          window.triggerNotification(success.error.message, 'error')
        } else {
          window.triggerNotification('Some error occurred', 'error')
        }
        yield* handleAuthError(success.error)
      }
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
    window.triggerNotification('Failed to invite collaborator', 'error')
  }
}

export function* TEAM_COLLABORATOR_REMOVE({ payload, removalType }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areTeamMembersLoading: true },
  })

  const activeApp = reduxStore.getState().apps.active

  if (!(activeApp.role !== undefined && (activeApp.role === 'admin' || activeApp.role === 'owner'))) {
    window.triggerNotification('Access Denied - you are not authorized to perform this action', 'error')
    yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
    return
  }

  try {
    const success = yield call(collaboratorRemove, payload, activeApp)

    if (success && success.data) {
      yield put({
        type: actions.TEAM_COLLABORATOR_LIST,
        payload: { currentPage: 1, data: activeApp },
      })

      let removeCollaboratorSuccess = false
      let removeCollaboratorActionMsg =
        'Collaborator removal is not successful. Please try refreshing the page and try again.'
      const collaboratorToRemoveEmail = payload.collaboratorsToRemove[0]

      if (
        success.data.collaboratorsToRemove &&
        success.data.collaboratorsToRemove[collaboratorToRemoveEmail] &&
        success.data.collaboratorsToRemove[collaboratorToRemoveEmail].success
      ) {
        removeCollaboratorSuccess = true
        removeCollaboratorActionMsg = removalType === 'remove' ? 'Collaborator removed' : 'Invitation cancelled'
      }

      if (removeCollaboratorSuccess) {
        window.triggerNotification(removeCollaboratorActionMsg, 'success')
      } else {
        window.triggerNotification(removeCollaboratorActionMsg, 'error')
      }
    } else if (success && success.error) {
      if (success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.code === 'ERR_ACCESS_DENIED') {
        window.triggerNotification('Access Denied - you are not authorized to perform this action', 'error')
      } else if (success.error.message) {
        window.triggerNotification(success.error.message, 'error')
      }
      yield* handleAuthError(success.error)
    }

    yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
    window.triggerNotification('Failed to remove collaborator', 'error')
  }
}

export function* TEAM_COLLABORATOR_CHANGE_ROLE({ payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areTeamMembersLoading: true },
  })

  const activeApp = reduxStore.getState().apps.active

  if (!(activeApp.role !== undefined && (isAppAdmin(activeApp) || isAppOwner(activeApp)))) {
    window.triggerNotification('Access Denied - you are not authorized to perform this action', 'error')
    yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
    yield put(push(`/app/${activeApp.id}/members`))
    return
  }

  try {
    const success = yield call(collaboratorChangeRole, payload, activeApp)

    if (success && success.data) {
      yield put({
        type: actions.TEAM_COLLABORATOR_LIST,
        payload: { currentPage: 1, data: activeApp },
      })

      let changeSuccess = false
      let changeMsg = 'Collaborator role update is not successful. Please try refreshing the page and try again.'

      if (payload.admins && payload.admins.length > 0) {
        const email = payload.admins[0]
        if (success.data.admins && success.data.admins[email] && success.data.admins[email].success && success.data.admins[email].changedRole) {
          changeSuccess = true
          changeMsg = 'Collaborator is now an admin'
        }
      } else if (payload.moderators && payload.moderators.length > 0) {
        const email = payload.moderators[0]
        if (success.data.moderators && success.data.moderators[email] && success.data.moderators[email].success && success.data.moderators[email].changedRole) {
          changeSuccess = true
          changeMsg = 'Collaborator is now a Moderator.'
        }
      } else if (payload.developers && payload.developers.length > 0) {
        const email = payload.developers[0]
        if (success.data.developers && success.data.developers[email] && success.data.developers[email].success && success.data.developers[email].changedRole) {
          changeSuccess = true
          changeMsg = 'Collaborator is now a Developer.'
        }
      }

      if (changeSuccess) {
        window.triggerNotification(changeMsg, 'success')
      } else {
        window.triggerNotification(changeMsg, 'error')
      }
      yield put(push(`/app/${activeApp.id}/members`))
    } else if (success && success.error) {
      if (success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.code === 'ERR_ACCESS_DENIED') {
        window.triggerNotification('Access Denied - you are not authorized to perform this action', 'error')
      } else if (success.error.message) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
      yield* handleAuthError(success.error)
      yield put(push(`/app/${activeApp.id}/members`))
    }

    yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
    window.triggerNotification('Failed to change collaborator role', 'error')
    yield put(push(`/app/${activeApp.id}/members`))
  }
}

export function* TEAM_COLLABORATOR_LEAVE() {
  yield put({
    type: actions.SET_STATE,
    payload: { areTeamMembersLoading: true },
  })

  const activeApp = reduxStore.getState().apps.active

  try {
    const success = yield call(collaboratorLeave, activeApp)

    if (success && success.data) {
      yield put({
        type: 'apps/ENTER_APP_TEXT',
        payload: { searchText: '', clearText: true },
      })
      yield put({
        type: 'apps/LIST',
        payload: { currentPage: 1 },
      })
      yield put(push('/apps'))
      window.triggerNotification('You are no longer a collaborator', 'success')
    } else if (success && success.error) {
      if (success.error.code === 'ERR_OWNER_LEAVE_DENIED') {
        window.triggerNotification('Access Denied - Owner is not allowed to leave the group', 'error')
      } else if (success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.message) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Some error occurred', 'error')
      }
      yield* handleAuthError(success.error)
    }

    yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
    window.triggerNotification('Failed to leave team', 'error')
  }
}

export function* TEAM_TRANSFER_OWNERSHIP({ payload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areTeamMembersLoading: true },
  })

  const activeApp = reduxStore.getState().apps.active

  if (!(activeApp.role !== undefined && activeApp.role === 'owner')) {
    window.triggerNotification('Access Denied - you are not authorized to perform this action', 'error')
    yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
    yield put(push(`/app/${activeApp.id}/members`))
    return
  }

  try {
    const success = yield call(collaboratorTransferOwnership, payload, activeApp)

    if (success && success.data) {
      const appData = yield call(get, activeApp.id)
      const allApps = reduxStore.getState().apps.data
      if (!_.isEmpty(allApps)) {
        const appIndex = _.findIndex(allApps, function fn(a) {
          return a.id === activeApp.id
        })
        if (appIndex >= 0) {
          allApps[appIndex].role = constants.teamRoles.admin
        }
      }
      yield put({
        type: actions.TEAM_COLLABORATOR_LIST,
        payload: { currentPage: 1, data: appData.data },
      })
      yield put(push(`/app/${activeApp.id}/members`))
      window.triggerNotification('App ownership transfer is successful', 'success')
    } else if (success && success.error) {
      if (success.error.code === 'ERR_BAD_REQUEST') {
        _.forEach(success.error.details, function fn(value) {
          message.error(value)
        })
      } else if (success.error.code === 'ERR_ACCESS_DENIED') {
        window.triggerNotification('Access Denied - you are not authorized to perform this action', 'error')
      } else if (success.error.message) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification('Transferring app ownership is not successful', 'error')
      }
      yield* handleAuthError(success.error)
      yield put(push(`/app/${activeApp.id}/members`))
    }

    yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
    window.triggerNotification('Failed to transfer ownership', 'error')
    yield put(push(`/app/${activeApp.id}/members`))
  }
}

export function* ENTER_TEAM_COLLABORATOR_TEXT({ payload }) {
  const { searchText, clearText } = payload
  if (clearText) {
    yield put({
      type: actions.SET_STATE,
      payload: { teamCollaboratorSearchText: '' },
    })
  } else {
    yield put({
      type: actions.SET_STATE,
      payload: { teamCollaboratorSearchText: searchText },
    })
    if (searchText.length === 0 || searchText.length >= 2) {
      yield put({
        type: actions.SEARCH_TEAM_COLLABORATOR,
        payload: {},
      })
    }
  }
}

export function* SEARCH_TEAM_COLLABORATOR() {
  yield put({
    type: actions.SET_STATE,
    payload: { areTeamMembersLoading: true },
  })

  const activeApp = reduxStore.getState().apps.active
  const userData = reduxStore.getState().user
  const { teamCollaboratorSearchText } = reduxStore.getState().teams

  try {
    const success = yield call(collaboratorsSearch, activeApp, teamCollaboratorSearchText)

    if (success && success.data) {
      let totalCollaboratorCount = success.data.length
      if (success.meta && success.meta.pagination && success.meta.pagination.total) {
        totalCollaboratorCount = success.meta.pagination.total
      }

      let sortedList = success.data
      if (sortedList.length > 0) {
        const loggedInCollaboratorIndex = _.findIndex(sortedList, function fn(sl) {
          return _.toLower(sl.email) === _.toLower(userData.email)
        })
        if (loggedInCollaboratorIndex > 0) {
          const loggedInCollaboratorValue = sortedList[loggedInCollaboratorIndex]
          _.pullAt(sortedList, [loggedInCollaboratorIndex])
          sortedList = _.concat(sortedList, loggedInCollaboratorValue)
          sortedList = _.reverse(sortedList)
        }
      }

      yield put({
        type: actions.SET_STATE,
        payload: {
          teamCollaboratorsList: sortedList,
          teamCollaboratorsListTotal: totalCollaboratorCount,
          areTeamMembersLoading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          teamCollaboratorsListTotal: 0,
          areTeamMembersLoading: false,
        },
      })
      if (success && success.error) {
        yield* handleAuthError(success.error)
      }
    }
  } catch (error) {
    yield put({ type: actions.SET_STATE, payload: { areTeamMembersLoading: false } })
    window.triggerNotification('Failed to search team members', 'error')
  }
}

export default function* teamsSaga() {
  yield all([
    takeEvery(actions.TEAM_COLLABORATOR_LIST, TEAM_COLLABORATOR_LIST),
    takeEvery(actions.TEAM_COLLABORATOR_INVITE, TEAM_COLLABORATOR_INVITE),
    takeEvery(actions.TEAM_COLLABORATOR_REMOVE, TEAM_COLLABORATOR_REMOVE),
    takeEvery(actions.TEAM_COLLABORATOR_CHANGE_ROLE, TEAM_COLLABORATOR_CHANGE_ROLE),
    takeEvery(actions.TEAM_COLLABORATOR_LEAVE, TEAM_COLLABORATOR_LEAVE),
    takeEvery(actions.TEAM_TRANSFER_OWNERSHIP, TEAM_TRANSFER_OWNERSHIP),
    takeEvery(actions.ENTER_TEAM_COLLABORATOR_TEXT, ENTER_TEAM_COLLABORATOR_TEXT),
    takeLatest(actions.SEARCH_TEAM_COLLABORATOR, SEARCH_TEAM_COLLABORATOR),
  ])
}
